/* Vault theme – conversion components */

/* ---------- Countdown ---------- */
if (!customElements.get('vault-countdown')) {
  class VaultCountdown extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'daily';
      this.end = this.getEnd();
      if (!this.end) return;
      this.days = this.querySelector('[data-days]');
      this.hours = this.querySelector('[data-hours]');
      this.minutes = this.querySelector('[data-minutes]');
      this.seconds = this.querySelector('[data-seconds]');
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    getEnd() {
      const now = new Date();
      if (this.mode === 'fixed') {
        const parsed = new Date(this.dataset.end);
        return isNaN(parsed) ? null : parsed;
      }
      if (this.mode === 'evergreen') {
        const minutes = parseInt(this.dataset.minutes, 10) || 15;
        const key = 'vault-countdown-' + (this.dataset.key || 'default');
        let stored = null;
        try {
          stored = parseInt(localStorage.getItem(key), 10);
        } catch (e) {}
        if (!stored || stored < now.getTime()) {
          stored = now.getTime() + minutes * 60000;
          try {
            localStorage.setItem(key, String(stored));
          } catch (e) {}
        }
        return new Date(stored);
      }
      // daily: endet täglich zur Cutoff-Stunde (Standard: Mitternacht)
      const cutoff = Math.min(24, Math.max(1, parseInt(this.dataset.cutoff, 10) || 24));
      const end = new Date(now);
      end.setHours(cutoff, 0, 0, 0);
      if (end <= now) end.setDate(end.getDate() + 1);
      return end;
    }

    tick() {
      let diff = Math.max(0, this.end.getTime() - Date.now());
      if (diff === 0) {
        if (this.mode === 'fixed') {
          if (this.dataset.hideOnEnd === 'true') this.closest('.vault-countdown-wrap')?.setAttribute('hidden', '');
          clearInterval(this.timer);
        } else {
          this.end = this.getEnd();
        }
      }
      const d = Math.floor(diff / 86400000);
      diff -= d * 86400000;
      const h = Math.floor(diff / 3600000);
      diff -= h * 3600000;
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff - m * 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.days) {
        this.days.textContent = pad(d);
        this.days.closest('[data-unit]')?.toggleAttribute('hidden', d === 0);
      }
      if (this.hours) this.hours.textContent = pad(h);
      if (this.minutes) this.minutes.textContent = pad(m);
      if (this.seconds) this.seconds.textContent = pad(s);
    }
  }
  customElements.define('vault-countdown', VaultCountdown);
}

/* ---------- Sticky Add to Cart ---------- */
if (!customElements.get('vault-sticky-atc')) {
  class VaultStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('[data-sticky-button]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.target = document.getElementById('ProductSubmitButton-' + this.sectionId);

      this.button?.addEventListener('click', () => this.onClick());

      this.variantSelect = this.querySelector('[data-sticky-variant]');
      this.picker = document.getElementById('variant-selects-' + this.sectionId);
      if (this.variantSelect && !this.picker) this.variantSelect.closest('.vault-sticky-atc__select')?.setAttribute('hidden', '');
      this.variantSelect?.addEventListener('change', () => this.applyVariant());

      const watched = this.target || document.getElementById('MainProduct-' + this.sectionId);
      if (watched && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', passed);
            document.body.classList.toggle('vault-sticky-atc-visible', passed);
          },
          { threshold: 0 }
        );
        this.observer.observe(watched);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const v = event?.data?.variant;
          if (this.variantSelect && v && String(v.id) !== this.variantSelect.value) this.variantSelect.value = String(v.id);
          setTimeout(() => this.sync(), 60);
        });
      }
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
    }

    sync() {
      this.target = document.getElementById('ProductSubmitButton-' + this.sectionId) || this.target;
      const mainPrice = document.querySelector('#price-' + this.sectionId + ' .price');
      if (mainPrice && this.priceEl) {
        const sale = mainPrice.querySelector('.price__sale .price-item--sale');
        const regular = mainPrice.querySelector('.price__regular .price-item--regular');
        const onSale = mainPrice.classList.contains('price--on-sale');
        this.priceEl.textContent = ((onSale ? sale : regular) || sale || regular)?.textContent.trim() || '';
      }
      if (this.target && this.button) {
        this.button.disabled = this.target.disabled;
        const label = this.target.querySelector('span');
        const price = this.dataset.bundleTotal || (this.priceEl ? this.priceEl.textContent.trim() : '');
        if (this.target.disabled && label) this.button.querySelector('span').textContent = label.textContent.trim();
        else if (this.dataset.priceInButton === 'true' && price) this.button.querySelector('span').textContent = price + ' • ' + this.dataset.label;
        else this.button.querySelector('span').textContent = this.dataset.label;
      }
    }

    applyVariant() {
      // Wählt dieselbe Variante im Haupt-Picker aus (Buttons oder Dropdowns), damit Preis, Bild & Formular mitziehen.
      this.picker = document.getElementById('variant-selects-' + this.sectionId) || this.picker;
      const opt = this.variantSelect?.selectedOptions[0];
      if (!this.picker || !opt) return;
      const values = (opt.dataset.options || '').split('||');
      const groups = this.picker.querySelectorAll('fieldset, .product-form__input--dropdown');
      values.forEach((value, i) => {
        const group = groups[i];
        if (!group) return;
        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        const select = group.querySelector('select');
        if (select && select.value !== value) {
          const match = Array.from(select.options).find((o) => o.value === value);
          if (match) {
            select.value = match.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    onClick() {
      // Klickt den echten Haupt-Button, damit Kaching Bundles & Co. ihre Logik behalten.
      const target = document.getElementById('ProductSubmitButton-' + this.sectionId);
      if (this.dataset.behavior === 'scroll' || !target) {
        (document.getElementById('MainProduct-' + this.sectionId) || document.body).scrollIntoView({ behavior: 'smooth' });
        return;
      }
      target.click();
    }
  }
  customElements.define('vault-sticky-atc', VaultStickyAtc);
}

/* ---------- Slider (UGC / Reviews) ---------- */
if (!customElements.get('vault-slider')) {
  class VaultSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));

      // Videos nur abspielen, wenn sichtbar (spart Daten & Akku)
      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              const v = e.target;
              if (e.isIntersecting) v.play?.().catch(() => {});
              else v.pause?.();
            });
          },
          { threshold: 0.5 }
        );
        this.querySelectorAll('video[data-autoplay]').forEach((v) => this.videoObserver.observe(v));
      }

      this.querySelectorAll('[data-sound-toggle]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const video = btn.closest('.vault-ugc__card')?.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          btn.classList.toggle('is-unmuted', !video.muted);
        })
      );
    }

    disconnectedCallback() {
      this.videoObserver?.disconnect();
    }

    scroll(dir) {
      const card = this.track.querySelector(':scope > *');
      const step = card ? card.getBoundingClientRect().width + 16 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
  }
  customElements.define('vault-slider', VaultSlider);
}

/* ---------- Laufband (Marquee) ---------- */
if (!customElements.get('vault-marquee')) {
  class VaultMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.group = this.querySelector('.vault-marquee__group');
      if (!this.track || !this.group) return;
      this.update = this.update.bind(this);
      this.update();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this);
      }
      // Fonts laden später nach – danach Breite neu messen
      document.fonts?.ready?.then(this.update);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    update() {
      // Gruppe so oft auffüllen, bis sie breiter als die Leiste ist (sonst entstehen Lücken)
      const groups = this.track.querySelectorAll('.vault-marquee__group');
      let guard = 0;
      while (this.group.scrollWidth < this.clientWidth && guard < 6) {
        const items = Array.from(this.group.children).slice(0, this.group.dataset.base ? Number(this.group.dataset.base) : undefined);
        if (!this.group.dataset.base) this.group.dataset.base = String(this.group.children.length);
        items.forEach((el) => {
          const c = el.cloneNode(true);
          c.removeAttribute('data-shopify-editor-block');
          this.group.appendChild(c);
        });
        guard++;
      }
      if (groups[1]) groups[1].innerHTML = this.group.innerHTML;
      const speed = Math.max(10, parseInt(this.dataset.speed, 10) || 50);
      this.style.setProperty('--vault-marquee-duration', this.group.scrollWidth / speed + 's');
    }
  }
  customElements.define('vault-marquee', VaultMarquee);
}

/* ---------- Scroll-Fortschritt + Nach-oben ---------- */
(function () {
  const bar = document.querySelector('[data-vault-progress]');
  const top = document.querySelector('[data-vault-totop]');
  if (!bar && !top) return;
  let ticking = false;
  const update = () => {
    const h = document.documentElement.scrollHeight - window.innerHeight;
    const p = h > 0 ? Math.min(1, window.scrollY / h) : 0;
    if (bar) bar.style.transform = 'scaleX(' + p + ')';
    if (top) top.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8);
    ticking = false;
  };
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  top?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  update();
})();

/* ---------- Lieferdatum ---------- */
if (!customElements.get('vault-delivery')) {
  class VaultDelivery extends HTMLElement {
    connectedCallback() {
      const min = Math.max(0, parseInt(this.dataset.min, 10) || 0);
      const max = Math.max(min, parseInt(this.dataset.max, 10) || min);
      const cutoff = parseInt(this.dataset.cutoff, 10) || 24;
      const skipWeekends = this.dataset.weekends === 'true';
      const start = new Date();
      if (start.getHours() >= cutoff) start.setDate(start.getDate() + 1);
      const add = (base, days) => {
        const d = new Date(base);
        let left = days;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          const wd = d.getDay();
          if (skipWeekends && (wd === 0 || wd === 6)) continue;
          left--;
        }
        return d;
      };
      let fmt;
      try {
        fmt = new Intl.DateTimeFormat(this.dataset.locale || 'en-US', { month: 'short', day: 'numeric' });
      } catch (e) {
        fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
      }
      const a = fmt.format(add(start, min));
      const b = fmt.format(add(start, max));
      const date = min === max ? a : a + ' – ' + b;
      const el = this.querySelector('[data-delivery-text]');
      const tpl = this.dataset.text || 'Order today and receive by [date]';
      if (el) el.innerHTML = tpl.replace('[date]', '<strong>' + date + '</strong>');
    }
  }
  customElements.define('vault-delivery', VaultDelivery);
}

/* ---------- Versand-Timeline ---------- */
if (!customElements.get('vault-timeline')) {
  class VaultTimeline extends HTMLElement {
    connectedCallback() {
      const n = (k, d) => {
        const v = parseInt(this.dataset[k], 10);
        return isNaN(v) ? d : v;
      };
      const skip = this.dataset.weekends === 'true';
      const start = new Date();
      if (start.getHours() >= n('cutoff', 24)) start.setDate(start.getDate() + 1);
      const add = (days) => {
        const d = new Date(start);
        let left = days;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          if (skip && (d.getDay() === 0 || d.getDay() === 6)) continue;
          left--;
        }
        return d;
      };
      const locale = this.dataset.locale || 'en';
      let fmt;
      try {
        fmt = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
      } catch (e) {
        fmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' });
      }
      const ord = (d) => {
        if (!/^en/i.test(locale)) return fmt.format(d);
        const day = d.getDate();
        const s = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
        return fmt.format(d) + s;
      };
      const range = (a, b) => (a === b ? ord(add(a)) : ord(add(a)) + ' – ' + ord(add(b)));
      const pMin = n('procMin', 1), pMax = Math.max(pMin, n('procMax', 3));
      const dMin = n('delMin', 7), dMax = Math.max(dMin, n('delMax', 12));
      const set = (i, t) => { const el = this.querySelector('[data-tl-date="' + i + '"]'); if (el) el.textContent = t; };
      set(1, ord(start));
      set(2, range(pMin, pMax));
      set(3, range(dMin, dMax));
    }
  }
  customElements.define('vault-timeline', VaultTimeline);
}
